import * as Sentry from "@sentry/react";

interface DatadogRumReporter {
    addError: (error: unknown, context?: Record<string, unknown>) => void;
    setUser: (user: Record<string, unknown>) => void;
    removeUserProperty: (key: string) => void;
    setGlobalContextProperty: (key: string, value: unknown) => void;
    removeGlobalContextProperty: (key: string) => void;
}

interface MonitoringIdentity {
    user: {
        id: number;
        email: string;
        firstname: string;
        lastname: string;
        roles: string[];
    };
    company?: {
        id: number;
        name: string;
        isProvider: boolean;
    };
}

interface NormalizedIdentityContext {
    userName: string;
    sentryUser: {
        id: string;
        email: string;
        username: string;
    };
    datadogUser: {
        id: string;
        email: string;
        name: string;
    };
    appIdentity: {
        user_id: string;
        user_email: string;
        user_name: string;
        user_roles: string[];
        company_id?: string;
        company_name?: string;
        company_is_provider?: boolean;
    };
}

interface ZodErrorReportContext {
    operation: string;
    endpoint?: string;
    expectedSchema?: string;
    summary: string;
    issueCount: number;
    issues: Array<{
        path: string;
        message: string;
        code: string;
        received: string;
        expected: string;
    }>;
}

// Datadog RUM is attached only after successful initialization.
let datadogRumReporter: DatadogRumReporter | undefined;
let monitoringIdentity: MonitoringIdentity | undefined;
let monitoringIdentitySignature: string | undefined;

const getBuildMetadata = (): { commitHash: string; buildDate: string } => {
    return {
        commitHash: import.meta.env.VITE_BUILD_COMMIT_HASH ?? "dev",
        buildDate: import.meta.env.VITE_BUILD_DATE ?? "unknown",
    };
};

const buildIdentityContext = (identity: MonitoringIdentity): NormalizedIdentityContext => {
    const userName = `${identity.user.firstname} ${identity.user.lastname}`.trim();

    return {
        userName,
        sentryUser: {
            id: String(identity.user.id),
            email: identity.user.email,
            username: userName,
        },
        datadogUser: {
            id: String(identity.user.id),
            email: identity.user.email,
            name: userName,
        },
        appIdentity: {
            user_id: String(identity.user.id),
            user_email: identity.user.email,
            user_name: userName,
            user_roles: identity.user.roles,
            ...(identity.company !== undefined ?
                {
                    company_id: String(identity.company.id),
                    company_name: identity.company.name,
                    company_is_provider: identity.company.isProvider,
                }
            :   {}),
        },
    };
};

const buildIdentitySignature = (identity: MonitoringIdentity | undefined): string | undefined => {
    if (identity === undefined) {
        return undefined;
    }

    return JSON.stringify({
        user: {
            id: identity.user.id,
            email: identity.user.email,
            firstname: identity.user.firstname,
            lastname: identity.user.lastname,
            roles: identity.user.roles,
        },
        company:
            identity.company === undefined ?
                undefined
            :   {
                    id: identity.company.id,
                    name: identity.company.name,
                    isProvider: identity.company.isProvider,
                },
    });
};

const registerDatadogBuildContext = (): void => {
    if (datadogRumReporter === undefined) {
        return;
    }

    const build = getBuildMetadata();
    datadogRumReporter.setGlobalContextProperty("build_commit_hash", build.commitHash);
    datadogRumReporter.setGlobalContextProperty("build_date", build.buildDate);
};

const clearDatadogIdentityContext = (): void => {
    datadogRumReporter?.removeUserProperty("id");
    datadogRumReporter?.removeUserProperty("email");
    datadogRumReporter?.removeUserProperty("name");
    datadogRumReporter?.removeGlobalContextProperty("user_id");
    datadogRumReporter?.removeGlobalContextProperty("user_email");
    datadogRumReporter?.removeGlobalContextProperty("user_name");
    datadogRumReporter?.removeGlobalContextProperty("company_id");
    datadogRumReporter?.removeGlobalContextProperty("company_name");
    datadogRumReporter?.removeGlobalContextProperty("company_is_provider");
    datadogRumReporter?.removeGlobalContextProperty("user_roles");
};

const registerDatadogIdentityContext = (identityContext: NormalizedIdentityContext): void => {
    if (datadogRumReporter === undefined) {
        return;
    }

    clearDatadogIdentityContext();
    datadogRumReporter.setUser(identityContext.datadogUser);
    datadogRumReporter.setGlobalContextProperty("user_id", identityContext.appIdentity.user_id);
    datadogRumReporter.setGlobalContextProperty("user_email", identityContext.appIdentity.user_email);
    datadogRumReporter.setGlobalContextProperty("user_name", identityContext.appIdentity.user_name);
    datadogRumReporter.setGlobalContextProperty("user_roles", identityContext.appIdentity.user_roles);

    if (identityContext.appIdentity.company_id !== undefined) {
        datadogRumReporter.setGlobalContextProperty("company_id", identityContext.appIdentity.company_id);
    }

    if (identityContext.appIdentity.company_name !== undefined) {
        datadogRumReporter.setGlobalContextProperty("company_name", identityContext.appIdentity.company_name);
    }

    if (identityContext.appIdentity.company_is_provider !== undefined) {
        datadogRumReporter.setGlobalContextProperty(
            "company_is_provider",
            identityContext.appIdentity.company_is_provider,
        );
    }
};

const getCurrentRoute = (): string | undefined => {
    if (typeof window === "undefined") {
        return undefined;
    }

    return window.location.pathname;
};

const getCurrentLocale = (): string | undefined => {
    if (typeof document !== "undefined" && document.documentElement.lang !== "") {
        return document.documentElement.lang;
    }

    if (typeof localStorage !== "undefined") {
        const storedLanguage = localStorage.getItem("language");
        if (storedLanguage !== null && storedLanguage !== "") {
            return storedLanguage;
        }
    }

    if (typeof navigator !== "undefined" && navigator.language !== "") {
        return navigator.language;
    }

    return undefined;
};

export const registerDatadogRum = (rum: DatadogRumReporter | undefined): void => {
    datadogRumReporter = rum;

    if (datadogRumReporter === undefined) {
        return;
    }

    registerDatadogBuildContext();

    if (monitoringIdentity !== undefined) {
        registerDatadogIdentityContext(buildIdentityContext(monitoringIdentity));
    }
};

export const registerMonitoringIdentity = (identity: MonitoringIdentity | undefined): void => {
    const identitySignature = buildIdentitySignature(identity);

    if (identitySignature === monitoringIdentitySignature) {
        return;
    }

    monitoringIdentity = identity;
    monitoringIdentitySignature = identitySignature;

    if (identity === undefined) {
        Sentry.setUser(null);

        clearDatadogIdentityContext();

        return;
    }

    const identityContext = buildIdentityContext(identity);

    Sentry.setUser(identityContext.sentryUser);

    registerDatadogIdentityContext(identityContext);
};

export const reportZodValidationError = (error: unknown, context: ZodErrorReportContext): void => {
    const build = getBuildMetadata();
    const route = getCurrentRoute();
    const locale = getCurrentLocale();
    const identityContext = monitoringIdentity !== undefined ? buildIdentityContext(monitoringIdentity) : undefined;
    const fingerprint = ["zod-validation", context.operation, context.endpoint ?? "api-response"];
    const appContext = {
        build,
        route,
        locale,
        ...(identityContext !== undefined ? { identity: identityContext.appIdentity } : {}),
    };
    const sentryContext = {
        ...context,
        ...appContext,
        kind: "zod_validation",
    } satisfies Record<string, unknown>;

    Sentry.withScope((scope) => {
        scope.setTag("error.kind", "zod_validation");
        scope.setTag("zod.operation", context.operation);
        scope.setTag("app.locale", locale ?? "unknown");

        if (identityContext?.appIdentity.company_id !== undefined) {
            scope.setTag("company.id", identityContext.appIdentity.company_id);
        }

        if (context.endpoint !== undefined) {
            scope.setTag("zod.endpoint", context.endpoint);
        }

        scope.setFingerprint(fingerprint);
        scope.setContext("app", appContext);
        scope.setContext("zod", sentryContext);
        Sentry.captureException(error);
    });

    datadogRumReporter?.addError(error, {
        kind: "zod_validation",
        operation: context.operation,
        endpoint: context.endpoint,
        expectedSchema: context.expectedSchema,
        ...appContext,
        summary: context.summary,
        issueCount: context.issueCount,
        issues: context.issues,
    });
};
